import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import Chart from 'react-apexcharts';

function Dashboard() {
  // State variables for charts and stats
  const [attacksCount, setAttacksCount] = useState(0);
  const [peakAttackPPS, setPeakAttackPPS] = useState(0);
  const [peakAttackSize, setPeakAttackSize] = useState(0);
  const [timeRangeLabel, setTimeRangeLabel] = useState('In the last 24 hours');

  // 1. Retrieve user and token from localStorage
  const storedUser = localStorage.getItem('user');
  const userObj = storedUser ? JSON.parse(storedUser) : null;
  const token = userObj?.token || '';

  useEffect(() => {
    // 2. Fetch attacks using the absolute URL and Authorization header
    fetch('https://backend.hybridprotect.hybridhosting.co/api/v1/account/attacks', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch attacks: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        console.log('Attacks from server:', data);

        const { filteredAttacks, label } = findRelevantAttacks(data);

        const count = filteredAttacks.length;
        const maxPPS = filteredAttacks.reduce(
          (acc, atk) => Math.max(acc, atk.peak_pps),
          0
        );
        const maxSize = filteredAttacks.reduce(
          (acc, atk) => Math.max(acc, atk.peak_bps),
          0
        );

        setAttacksCount(count);
        setPeakAttackPPS(maxPPS);
        setPeakAttackSize(maxSize);
        setTimeRangeLabel(count > 0 ? label : 'All time');
      })
      .catch((err) => {
        console.error('Error fetching attacks:', err);
      });
  }, [token]);

  /**
   * Filters attacks based on predefined time intervals.
   * Checks each interval in ascending order and returns the first matching set.
   */
  function findRelevantAttacks(allAttacks) {
    const now = Date.now();
    const intervals = [
      { hours: 24,       label: '24 hours' },
      { hours: 48,       label: '48 hours' },
      { hours: 24 * 7,   label: '7 days' },
      { hours: 24 * 30,  label: '30 days' },
      { hours: 24 * 90,  label: '3 months' },
      { hours: 24 * 365, label: '1 year' }
    ];

    for (const interval of intervals) {
      const cutoff = now - interval.hours * 3600 * 1000;
      const filtered = allAttacks.filter((a) => {
        const attackStart = new Date(a.start).getTime();
        return attackStart >= cutoff;
      });

      if (filtered.length > 0) {
        return { filteredAttacks: filtered, label: `In the last ${interval.label}` };
      }
    }

    // Fallback if no attacks found in any bracket
    return { filteredAttacks: allAttacks, label: 'All time' };
  }

  // Helper: Format large numbers w/ suffixes
  function formatNumber(num) {
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'k';
    return num.toString();
  }

  // Helper: Format attack size in bps, Kbps, Mbps, Gbps
  function formatAttackSize(bps) {
    if (bps <= 0) return '0 bps';
    if (bps < 1e3) return `${bps.toFixed(2)} bps`;
    if (bps < 1e6) return `${(bps / 1e3).toFixed(2)} Kbps`;
    if (bps < 1e9) return `${(bps / 1e6).toFixed(2)} Mbps`;
    return `${(bps / 1e9).toFixed(2)} Gbps`;
  }

  // Charts
  const lineChartOptions = {
    chart: {
      id: 'traffic-by-application',
      toolbar: { show: false }
    },
    xaxis: { categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'] },
    stroke: { curve: 'smooth' },
    dataLabels: { enabled: false },
    colors: ['#FF4560', '#00E396', '#008FFB']
  };

  const lineChartSeries = [
    { name: 'Proxmox', data: [31, 40, 28, 51, 42, 109] },
    { name: 'Minecraft', data: [11, 32, 45, 32, 34, 52] },
    { name: 'Speedtest.org', data: [15, 11, 32, 18, 9, 24] }
  ];

  // Pie chart
  const pieChartOptions = {
    chart: { id: 'incoming-ports', type: 'pie' },
    labels: ['25565', '443', '8080', '19132', '8006', '21'],
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: { width: 200 },
          legend: { position: 'bottom' }
        }
      }
    ]
  };

  const pieChartSeries = [21, 22, 10, 28, 12, 7];

  return (
    <Layout>
      <div className="p-4">
        <h1 className="text-4xl font-bold text-white mb-8">Dashboard</h1>

        {/* Top Stats Section (Unblurred except "Risk") */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Attacks */}
          <div className="bg-dark-card p-5 rounded-lg shadow-lg">
            <h2 className="text-white text-lg">Attacks</h2>
            <p className="text-white text-3xl font-bold">{attacksCount}</p>
            <p className="text-gray-400 text-sm">{timeRangeLabel}</p>
          </div>

          {/* Peak Attack PPS */}
          <div className="bg-dark-card p-5 rounded-lg shadow-lg">
            <h2 className="text-white text-lg">Peak Attack PPS</h2>
            <p className="text-white text-3xl font-bold">
              {formatNumber(peakAttackPPS)} pkt/s
            </p>
            <p className="text-gray-400 text-sm">{timeRangeLabel}</p>
          </div>

          {/* Peak Attack Size */}
          <div className="bg-dark-card p-5 rounded-lg shadow-lg">
            <h2 className="text-white text-lg">Peak Attack Size</h2>
            <p className="text-white text-3xl font-bold">
              {formatAttackSize(peakAttackSize)}
            </p>
            <p className="text-gray-400 text-sm">{timeRangeLabel}</p>
          </div>

          {/* Risk (Blurred) */}
          <div className="relative overflow-hidden bg-dark-card p-5 rounded-lg shadow-lg">
            {/* Overlay */}
            <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-10">
              <span className="text-white text-3xl font-bold">Coming Soon</span>
            </div>
            {/* Blurred content */}
            <div className="blur-sm">
              <h2 className="text-white text-lg">Risk</h2>
              <p className="text-white text-3xl font-bold">2.4%</p>
              <p className="text-gray-400 text-sm">Service is Low Risk</p>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* Attacks Chart (Blurred) */}
          <div className="relative overflow-hidden bg-dark-card p-5 rounded-lg shadow-lg">
            {/* Overlay */}
            <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-10">
              <span className="text-white text-3xl font-bold">Coming Soon</span>
            </div>
            {/* Blurred content */}
            <div className="blur-sm">
              <h2 className="text-white text-2xl font-bold">Attacks</h2>
              <Chart
                options={lineChartOptions}
                series={lineChartSeries}
                type="line"
                height="210"
              />
            </div>
          </div>

          {/* Port Usage (Blurred) */}
          <div className="relative overflow-hidden bg-dark-card p-5 rounded-lg shadow-lg">
            {/* Overlay */}
            <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-10">
              <span className="text-white text-3xl font-bold">Coming Soon</span>
            </div>
            {/* Blurred content */}
            <div className="blur-sm">
              <h2 className="text-white text-2xl font-bold">Port Usage</h2>
              <Chart
                options={pieChartOptions}
                series={pieChartSeries}
                type="pie"
                height="210"
              />
            </div>
          </div>
        </div>

        {/* Vulnerabilities and Risks Section */}
        <div className="flex flex-col lg:flex-row gap-4 mb-">
          <div className="flex flex-col gap-4 w-full lg:w-1/2">
            {/* Vulnerabilities Card (Blurred) */}
            <div className="relative overflow-hidden bg-dark-card p-5 rounded-lg shadow-lg">
              {/* Overlay */}
              <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-10">
                <span className="text-white text-3xl font-bold">Coming Soon</span>
              </div>
              {/* Blurred content */}
              <div className="blur-sm">
                <h2 className="text-white text-2xl font-bold">
                  There are 0 Urgent Vulnerabilities
                </h2>
                <button className="mt-7 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-300">
                  Manage Vulnerability Tolerance
                </button>
              </div>
            </div>

            {/* Detected Applications Card (Blurred) */}
            <div className="relative overflow-hidden bg-dark-card p-5 rounded-lg shadow-lg">
              {/* Overlay */}
              <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-10">
                <span className="text-white text-3xl font-bold">Coming Soon</span>
              </div>
              {/* Blurred content */}
              <div className="blur-sm">
                <h2 className="text-white text-2xl font-bold">
                  Detected Applications are Not Protected
                </h2>
                <button className="mt-7 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition duration-300">
                  Fix with Application Protect
                </button>
              </div>
            </div>
          </div>

          {/* Risk Assessment Card (Blurred) */}
          <div className="relative overflow-hidden bg-dark-card p-5 rounded-lg shadow-lg w-full lg:w-1/2 lg:col-span-1">
            {/* Overlay */}
            <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-60 flex items-center justify-center z-10">
              <span className="text-white text-3xl font-bold">Coming Soon</span>
            </div>
            {/* Blurred content */}
            <div className="blur-sm">
              <h2 className="text-white text-2xl font-bold">Risk Assessment</h2>
              <Chart
                options={lineChartOptions}
                series={lineChartSeries}
                type="line"
                height="210"
              />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;